import { closeModal, openModal } from './agent';
import { get, openInNewTab } from '@libs/shared';
import {
    WORKER_ERROR_MESSAGE,
    WORKER_FUNC,
    WORKER_FUNC_MAX_COUNT,
    WORKER_MESSAGE_TYPE
} from './constants';

// 加载worker的代码字符串
import workerCode from './workers/form.worker.js?raw-file';

/**
 * 创建表单 worker 实例。
 * 使用 Blob URL 方式创建 Worker，避免cdn的跨域问题
 * @returns Worker 实例
 */
function createFormWorker() {
    const blob = new Blob([workerCode], { type: 'application/javascript' });
    const url = URL.createObjectURL(blob);
    const worker = new Worker(url);
    // Worker 创建后立即释放 Blob URL
    URL.revokeObjectURL(url);
    return worker;
}

/**
 * 执行worker
 *
 * @param root0
 * @param root0.code
 * @param root0.relyData
 * @param root0.onError
 * @param root0.onOver
 * @param root0.onCallFunction
 */
export const startAppletWorker = ({
    code,
    relyData,
    onCallFunction,
    onError,
    onOver,
    baseUrl,
    urlResolver
}: {
    code: string;
    relyData: object;
    baseUrl: string;
    urlResolver?: (url: string) => string;
    onCallFunction: (name: string, data: Object) => Promise<any>;
    onError: (message: string) => void;
    onOver: (res: object, debugRes: { stdout: string; stderr: string }) => void;
}) => {
    let workerModal: undefined | string;
    let workerFuncCount = 0;
    const worker = createFormWorker();
    worker.postMessage({
        type: WORKER_MESSAGE_TYPE.RUN,
        props: {
            code,
            schema: { triggerConf: relyData },
            env: { baseUrl }
        }
    });

    const callResultToWorker = ({
        id,
        method,
        result
    }: {
        id: number;
        method: string;
        result?: unknown;
    }) => {
        worker.postMessage({
            type: WORKER_MESSAGE_TYPE.CALL_RESULT,
            id,
            props: {
                method,
                result
            }
        });
    };

    worker.onmessage = (e) => {
        const data = e.data;
        const { type, props, id } = data;
        switch (type) {
            case WORKER_MESSAGE_TYPE.CALL:
                switch (props.method) {
                    case WORKER_FUNC.OPEN_MODAL:
                        if (!workerModal) {
                            // 打开模态框
                            openModal({
                                title: props.title,
                                url: props.url,
                                onSuccess: (modal) => {
                                    workerModal = modal;
                                },
                                onClose: () => {
                                    workerModal = undefined;
                                    callResultToWorker({
                                        id,
                                        method: props.method
                                    });
                                },
                                onMessage: (result: unknown) => {
                                    worker.postMessage({
                                        type: WORKER_MESSAGE_TYPE.UI_MESSAGE,
                                        props: {
                                            result
                                        }
                                    });
                                }
                            });
                        }
                        break;
                    case WORKER_FUNC.CLOSE_MODAL: {
                        // 关闭模态框
                        workerModal && closeModal(workerModal);
                        workerModal = undefined;
                        callResultToWorker({
                            id,
                            method: WORKER_FUNC.OPEN_MODAL
                        });
                        break;
                    }
                    case WORKER_FUNC.CALL_FUNCTION: {
                        // 函数调用
                        if (workerFuncCount >= WORKER_FUNC_MAX_COUNT) {
                            onError(WORKER_ERROR_MESSAGE.CALL_FUNC_OVER);
                            worker.terminate();
                        } else {
                            workerFuncCount++;
                            onCallFunction(props.name, props.data).then(
                                (result) => {
                                    const errors = get(result, 'data.errors');
                                    if (errors) {
                                        callResultToWorker({
                                            id,
                                            method: props.method,
                                            result: {
                                                message: errors.message
                                            }
                                        });
                                    } else {
                                        const data = get(result, 'data.return');
                                        callResultToWorker({
                                            id,
                                            method: props.method,
                                            result: {
                                                result: data
                                            }
                                        });
                                    }
                                },
                                (error) => {
                                    const data = get(error, 'response.data');
                                    onError(data.msg);
                                    worker.terminate();
                                }
                            );
                        }
                        break;
                    }
                    case WORKER_FUNC.OPEN_URL: {
                        openInNewTab(props.url && urlResolver ? urlResolver(props.url) : props.url);
                        break;
                    }
                }
                break;
            case WORKER_MESSAGE_TYPE.OVER: {
                // 执行回写
                const { result, stdout, stderr } = data.props;
                onOver(result, { stdout, stderr });
                worker.terminate();
                break;
            }
            case WORKER_MESSAGE_TYPE.ERROR: {
                // 代码执行错误
                onError(data.error.message);
                worker.terminate();
                break;
            }
        }
    };

    worker.onerror = (e) => {
        onError(e.error?.message);
        worker.terminate();
    };

    const clearWorker = () => {
        workerModal && closeModal(workerModal);
        worker.terminate();
    };
    return clearWorker;
};
